import * as yup from 'yup';

const optionalNumberField = (typeErrorMessage) =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null || originalValue === undefined
        ? undefined
        : value
    )
    .typeError(typeErrorMessage)
    .notRequired();

export const dataEntrySchema = yup.object().shape({
  // Date and Time validation
  date: yup
    .string()
    .required('Date is required')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (YYYY-MM-DD)'),
  time: yup
    .string()
    .required('Time is required')
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:mm)'),

  // Raw Water Intake
  raw_intake: yup
    .number()
    .typeError('Raw intake must be a number')
    .required('Raw intake is required')
    .min(0, 'Raw intake must be greater than or equal to 0'),
  pump_status: yup.string(),

  // Raw Water Quality
  turbidity: yup
    .number()
    .typeError('Turbidity must be a number')
    .required('Turbidity is required')
    .min(0, 'Turbidity must be greater than or equal to 0'),
  ph: yup
    .number()
    .typeError('pH must be a number')
    .required('pH is required')
    .min(0, 'pH must be between 0 and 14')
    .max(14, 'pH must be between 0 and 14'),
  conductivity: yup
    .number()
    .typeError('Conductivity must be a number')
    .required('Conductivity is required')
    .min(0, 'Conductivity must be greater than or equal to 0'),

  // Chemical Dosing (optional fields)
  pre_lime_dosage: optionalNumberField('Pre-lime dosage must be a number')
    .min(0, 'Pre-lime dosage must be more than 0 ')
    .max(10, 'Pre-lime dosage must be less than 10 ppm'),
  
  pre_lime_flow: optionalNumberField('Pre-lime flow must be a number')
    .min(0, 'Pre-lime flow must be more than 0 ')
    .max(10, 'Pre-lime flow must be less than 10 l/m'),
    

  alum_dosage: optionalNumberField('Alum dosage must be a number')
    .min(0, 'Alum dosage must be more than 0 ')
    .max(10, 'Alum dosage must be less than 10 ppm'),

  alum_strength: optionalNumberField('Alum strength must be a number')
    .min(0, 'Alum strength must be more than 0 ')
    .max(10, 'Alum strength must be less than 10 %'),

  post_lime_sph1_dosage: optionalNumberField('Post-lime SPH1 dosage must be a number')
    .min(0, 'Post-lime SPH1 dosage must be more than 0 ')
    .max(10, 'Post-lime SPH1 dosage must be less than 10 ppm'),

  post_lime_sph1_flow: optionalNumberField('Post-lime SPH1 flow must be a number')
    .min(0, 'Post-lime SPH1 flow must be more than 0 ')
    .max(15, 'Post-lime SPH1 flow must be less than 15 l/m'),

  post_lime_sph2_dosage: optionalNumberField('Post-lime SPH2 dosage must be a number')
    .min(0, 'Post-lime SPH2 dosage must be more than 0 ')
    .max(10, 'Post-lime SPH2 dosage must be less than 10 ppm'),

  post_lime_sph2_flow: optionalNumberField('Post-lime SPH2 flow must be a number')
    .min(0, 'Post-lime SPH2 flow must be more than 0 ')
    .max(15, 'Post-lime SPH2 flow must be less than 15 l/m'),


  // Chlorine Dosing
  pre_chlorine_dosage: yup
    .number()
    .typeError('Pre-chlorine dosage must be a number')
    .required('Pre-chlorine dosage is required')
    .min(0, 'Pre-chlorine dosage must be greater than or equal to 0'),
  pre_chlorine_usage: yup
    .number()
    .typeError('Pre-chlorine usage must be a number')
    .required('Pre-chlorine usage is required')
    .min(0, 'Pre-chlorine usage must be greater than or equal to 0'),
  post_chlorine_dosage: yup
    .number()
    .typeError('Post-chlorine dosage must be a number')
    .required('Post-chlorine dosage is required')
    .min(0, 'Post-chlorine dosage must be greater than or equal to 0'),
  post_chlorine_usage: yup
    .number()
    .typeError('Post-chlorine usage must be a number')
    .required('Post-chlorine usage is required')
    .min(0, 'Post-chlorine usage must be greater than or equal to 0'),
  chlorine_tank_level: optionalNumberField('Tank level must be a number')
    .min(0, 'Tank level must be between 0 and 100')
    .max(100, 'Tank level must be between 0 and 100'),

  // Settled Water Quality (optional)
  settled_pul_tube_turb: optionalNumberField('Turbidity must be a number')
  .min(0, 'Settled pul-tube must be greater than or equal to 0'),

  settled_pul_tube_ph: optionalNumberField('pH must be a number')
  .min(0, 'Settled pul-tube pH must be greater than 0 '),

  settled_pulsator_turb: optionalNumberField('Turbidity must be a number')
  .min(0, 'Settled pulsator turbidity must be greater than 0'),

  settled_pulsator_ph: optionalNumberField('pH must be a number')
  .min(0, 'Settled pulsator pH must be greater than 0 '),

  settled_cf1_turb: optionalNumberField('Turbidity must be a number')
  .min(0, 'Settled CF1 turbidity must be greater than 0'),

  settled_cf1_ph: optionalNumberField('pH must be a number')
  .min(0, 'Settled CF1 pH must be greater than 0 '),

  settled_cf2_turb: optionalNumberField('Turbidity must be a number')
  .min(0, 'Settled CF2 turbidity must be greater than 0'),

  settled_cf2_ph: optionalNumberField('pH must be a number')
  .min(0, 'Settled CF2 pH must be greater than 0 '),

  settled_pt1_turb: optionalNumberField('Turbidity must be a number')
  .min(0, 'Settled PT1 turbidity must be greater than 0'),

  settled_pt1_ph: optionalNumberField('pH must be a number')
  .min(0, 'Settled PT1 pH must be greater than 0 '),
  
  settled_pt2_turb: optionalNumberField('Turbidity must be a number')
  .min(0, 'Settled PT2 turbidity must be greater than 0'),
  settled_pt2_ph: optionalNumberField('pH must be a number')
  .min(0, 'Settled PT2 pH must be greater than 0 '),

  // Filtered Water (optional)
  filtered_turbidity: optionalNumberField('Turbidity must be a number'),
  filtered_ph: optionalNumberField('pH must be a number'),

  // Final Water Quality
  final_turbidity: yup
    .number()
    .typeError('Final turbidity must be a number')
    .required('Final turbidity is required')
    .min(0, 'Final turbidity must be greater than or equal to 0'),
  final_ph: yup
    .number()
    .typeError('Final pH must be a number')
    .required('Final pH is required')
    .min(0, 'Final pH must be between 0 and 14')
    .max(14, 'Final pH must be between 0 and 14'),
  residual_chlorine: yup
    .number()
    .typeError('Residual chlorine must be a number')
    .required('Residual chlorine is required')
    .min(0.2, 'Residual chlorine must be at least 0.2 mg/L')
    .max(1.0, 'Residual chlorine must not exceed 1.0 mg/L')
    .test('between', 'Residual chlorine must be between 0.2 and 1.0 mg/L', function(value) {
      if (value === null || value === undefined) return false;
      return value >= 0.2 && value <= 1.0;
    }),

  // Cross-field validation: residual_chlorine <= post_chlorine_dosage
  // This will be validated in a when/test after both fields are defined

  // Supporting Info
  operator_name: yup.string(),
  notes: yup.string().max(500, 'Notes must not exceed 500 characters'),
  confirm_accuracy: yup
    .boolean()
    .required('You must confirm accuracy')
    .oneOf([true], 'You must confirm that all data entered is accurate'),
});

// Custom validation to check residual_chlorine <= chlorine_dosage
export const validateChlorineConstraint = (values) => {
  const errors = {};
  
  if (
    values.residual_chlorine !== undefined &&
    values.post_chlorine_dosage !== undefined &&
    values.residual_chlorine > values.post_chlorine_dosage
  ) {
    errors.residual_chlorine =
      'Residual chlorine must be less than or equal to chlorine dosage';
  }

  return errors;
};
